import {
  type AdminSubscription,
  ApiError,
  billingIntervalUnit,
  type ChargeableCard,
  formatMoney,
  queryKeys,
  type SubscriptionPlanOption,
} from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  humanizeStatus,
  Input,
  SUBSCRIPTION_STATUS_TONE,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/** The browser-local calendar day of an ISO moment, as YYYY-MM-DD (what a date input holds). */
function localDay(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function tomorrowDay(): string {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  return localDay(d.toISOString())
}

export function dollarsToCents(text: string): number | null {
  const trimmed = text.trim().replace(/^\$/, '')
  if (trimmed === '') return null
  const n = Number(trimmed)
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null
}

function centsToDollars(cents: number | null): string {
  return cents === null ? '' : (cents / 100).toFixed(2)
}

/** "Profit $12.00 / month" — the plan's price minus what we pay; null until a cost is on record. */
export function profitText(
  sub: Pick<AdminSubscription, 'profitCents' | 'billingInterval'>,
): string | null {
  if (sub.profitCents === null) return null
  const sign = sub.profitCents < 0 ? '-' : ''
  return `Profit ${sign}${formatMoney(Math.abs(sub.profitCents))} / ${billingIntervalUnit(sub.billingInterval)}`
}

/** "$25.00 / month" */
export function planPrice(sub: Pick<AdminSubscription, 'monthlyCents' | 'billingInterval'>) {
  return `${formatMoney(sub.monthlyCents)} / ${billingIntervalUnit(sub.billingInterval)}`
}

/** What the plan's date means right now: when it renews, or when it ends. */
export function renewalText(sub: AdminSubscription): string {
  if (sub.status === 'cancelled') {
    return sub.cancelledAt ? `Cancelled ${formatDate(sub.cancelledAt)}` : 'Cancelled'
  }
  if (!sub.currentPeriodEnd) return '—'
  const date = formatDate(sub.currentPeriodEnd)
  return sub.cancelAtPeriodEnd ? `Ends ${date}` : `Renews ${date}`
}

/** "Charge moved to Sat, Oct 24, 10:00 PM" — set while a renewal is parked past, or
 *  brought forward of, Shabbos / Yom Tov (t1129); null otherwise. */
export function chargeShiftText(sub: AdminSubscription): string | null {
  if (!sub.chargeShiftKind || !sub.chargeShiftAt) return null
  const when = new Date(sub.chargeShiftAt).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
  if (sub.chargeShiftKind === 'combined') {
    return `Charged together with this customer’s other plans on ${when}`
  }
  return `Charge moved to ${when}`
}

export function SubscriptionStatusBadge({ sub }: { sub: AdminSubscription }) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      <Badge tone={SUBSCRIPTION_STATUS_TONE[sub.status] ?? 'neutral'}>
        {humanizeStatus(sub.status)}
      </Badge>
      {sub.cancelAtPeriodEnd && sub.status !== 'cancelled' ? (
        <Badge tone="warning">Cancelling</Badge>
      ) : null}
    </div>
  )
}

function useRefreshSubscriptions() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.all() })
}

/** "Profit: $12.00" for the price and the cost typed so far. */
export function costProfitHint(priceCents: number, cost: string): string {
  const cents = dollarsToCents(cost)
  if (cents === null) return ''
  const profit = priceCents - cents
  return `Profit: ${profit < 0 ? '-' : ''}${formatMoney(Math.abs(profit))}.`
}

/** Providers already used on any plan, offered as suggestions while typing. */
export function ProviderSuggestions() {
  const { data } = useQuery({
    queryKey: queryKeys.subscriptions.list({ providers: true }),
    queryFn: () =>
      api.get<{ items: AdminSubscription[] }>('/subscriptions', { query: { pageSize: 500 } }),
    staleTime: 60_000,
  })
  const names = [...new Set((data?.items ?? []).map((i) => i.provider).filter(Boolean))]
  return (
    <datalist id="plan-providers">
      {names.map((name) => (
        <option key={name} value={name as string} />
      ))}
    </datalist>
  )
}

export function EditSubscriptionDialog({
  sub,
  onClose,
}: {
  sub: AdminSubscription | null
  onClose: () => void
}) {
  const toast = useToast()
  const refresh = useRefreshSubscriptions()
  const [label, setLabel] = useState('')
  const [phoneLine, setPhoneLine] = useState('')
  const [simIccid, setSimIccid] = useState('')
  const [provider, setProvider] = useState('')
  const [device, setDevice] = useState('')
  const [activatedAt, setActivatedAt] = useState('')
  const [cost, setCost] = useState('')
  const [renews, setRenews] = useState(true)
  const [billingDate, setBillingDate] = useState('')
  const [confirmingSwitch, setConfirmingSwitch] = useState(false)
  const [choosingCard, setChoosingCard] = useState(false)
  const [cardId, setCardId] = useState('')

  useEffect(() => {
    if (sub) {
      setConfirmingSwitch(false)
      setChoosingCard(false)
      setCardId('')
      setLabel(sub.label ?? '')
      setPhoneLine(sub.phoneLine ?? '')
      setSimIccid(sub.simIccid ?? '')
      setProvider(sub.provider ?? '')
      setDevice(sub.device ?? '')
      setActivatedAt(sub.activatedAt ?? '')
      setCost(centsToDollars(sub.costCents))
      setRenews(!sub.cancelAtPeriodEnd)
      setBillingDate(sub.currentPeriodEnd ? localDay(sub.currentPeriodEnd) : '')
    }
  }, [sub])

  // Mirrors the server: only a live plan can change how it is billed, and the
  // date only moves while it is active (not past due, incomplete or mid-shift).
  const canChangeBilling = sub !== null && sub.status !== 'cancelled'
  const canMoveDate = canChangeBilling && sub.status === 'active' && !sub.chargeShiftKind
  const dateChanged =
    canMoveDate &&
    billingDate !== '' &&
    billingDate !== (sub.currentPeriodEnd ? localDay(sub.currentPeriodEnd) : '')
  const renewsChanged = canChangeBilling && renews !== !sub.cancelAtPeriodEnd

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/subscriptions/${sub?.id}`, {
        label: label.trim() || null,
        phoneLine: phoneLine.trim() || null,
        simIccid: simIccid.trim() || null,
        provider: provider.trim() || null,
        device: device.trim() || null,
        activatedAt: activatedAt || null,
        costCents: dollarsToCents(cost),
        ...(dateChanged ? { nextBillingDate: billingDate } : {}),
        ...(renewsChanged ? { renews } : {}),
      }),
    onSuccess: async () => {
      await refresh()
      toast.success('Plan updated')
      onClose()
    },
    onError: (error) =>
      toast.error(
        'Could not update the plan',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  // t1315: mirrors the server — only a settled, Stripe-billed plan can be moved off Stripe.
  const canSwitchBilling =
    sub !== null && sub.billedByStripe && sub.status === 'active' && !sub.chargeShiftKind

  // t1381: and the way back — a plan billed by hand can go onto one of the customer's own cards.
  const canSwitchToCard =
    sub !== null &&
    !sub.billedByStripe &&
    sub.status === 'active' &&
    !sub.chargeShiftKind &&
    !sub.cancelAtPeriodEnd &&
    sub.currentPeriodEnd !== null &&
    new Date(sub.currentPeriodEnd).getTime() > Date.now() + 60_000
  const { data: cards } = useQuery({
    queryKey: queryKeys.customers.paymentMethods(sub?.customerId ?? ''),
    queryFn: () => api.get<ChargeableCard[]>(`/customers/${sub?.customerId}/payment-methods`),
    enabled: choosingCard && sub !== null,
  })
  // Only the customer's own cards: a card subscription renews on its own Stripe customer.
  const ownCards = (cards ?? []).filter((c) => c.owner === null)
  const chosenCard = ownCards.find((c) => c.id === cardId)

  const switchToCard = useMutation({
    mutationFn: () =>
      api.post(`/subscriptions/${sub?.id}/bill-with-card`, { paymentMethodId: cardId }),
    onSuccess: async () => {
      await refresh()
      toast.success('Plan will now be charged on the card')
      onClose()
    },
    onError: (error) =>
      toast.error(
        'Could not switch the plan',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const switchBilling = useMutation({
    mutationFn: () => api.post(`/subscriptions/${sub?.id}/bill-elsewhere`, {}),
    onSuccess: async () => {
      await refresh()
      toast.success('Plan is now billed elsewhere')
      onClose()
    },
    onError: (error) =>
      toast.error(
        'Could not switch the plan',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog open={sub !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit plan</DialogTitle>
          <DialogDescription>
            {sub?.planName}
            {sub?.customerName ? ` · ${sub.customerName}` : ''}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Name" hint="Who this line is for, e.g. Mrs Smith.">
            {(props) => (
              <Input
                {...props}
                maxLength={80}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            )}
          </Field>
          <Field label="Phone line" hint="The number this plan pays for.">
            {(props) => (
              <Input
                {...props}
                type="tel"
                inputMode="tel"
                maxLength={30}
                value={phoneLine}
                onChange={(e) => setPhoneLine(e.target.value)}
              />
            )}
          </Field>
          <Field label="SIM (ICCID)">
            {(props) => (
              <Input
                {...props}
                maxLength={40}
                value={simIccid}
                onChange={(e) => setSimIccid(e.target.value)}
              />
            )}
          </Field>
          <Field label="Provider" hint="Who supplies the service, e.g. US Mobile.">
            {(props) => (
              <Input
                {...props}
                maxLength={60}
                list="plan-providers"
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
              />
            )}
          </Field>
          <ProviderSuggestions />
          <Field label="Activated device">
            {(props) => (
              <Input
                {...props}
                maxLength={120}
                value={device}
                onChange={(e) => setDevice(e.target.value)}
              />
            )}
          </Field>
          <Field label="Activation date">
            {(props) => (
              <Input
                {...props}
                type="date"
                value={activatedAt}
                onChange={(e) => setActivatedAt(e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Our cost ($)"
            hint={
              sub
                ? `What we pay the provider each ${billingIntervalUnit(sub.billingInterval)}. ${costProfitHint(sub.monthlyCents, cost)}`
                : undefined
            }
          >
            {(props) => (
              <Input
                {...props}
                inputMode="decimal"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
              />
            )}
          </Field>
          {canChangeBilling ? (
            <div className="space-y-3 border-t border-border pt-4">
              <p className="text-sm font-medium">Billing</p>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="plan-renews"
                  checked={renews}
                  onChange={() => setRenews(true)}
                  className="mt-0.5 size-4 accent-primary"
                />
                <span>
                  <span className="font-medium">Renews every {sub?.billingInterval}</span> — charged
                  again on each billing date.
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="plan-renews"
                  checked={!renews}
                  onChange={() => setRenews(false)}
                  className="mt-0.5 size-4 accent-primary"
                />
                <span>
                  <span className="font-medium">One-time</span> — ends on the billing date, with no
                  further charges.
                </span>
              </label>
              <Field
                label={renews ? 'Next billing date' : 'Ends on'}
                hint={
                  !canMoveDate
                    ? 'The date can only be moved on an active plan with no charge being rescheduled.'
                    : sub?.billedByStripe
                      ? 'Nothing is charged or refunded now; the full plan price is charged on the new date.'
                      : "This plan isn't billed through Stripe — this only changes the date on record."
                }
              >
                {(props) => (
                  <Input
                    {...props}
                    type="date"
                    min={tomorrowDay()}
                    disabled={!canMoveDate}
                    value={billingDate}
                    onChange={(e) => setBillingDate(e.target.value)}
                  />
                )}
              </Field>
              <div className="space-y-2 rounded-md border border-border p-3">
                <p className="text-sm font-medium">How it's charged</p>
                <p className="text-xs text-foreground-muted">
                  {sub?.billedByStripe
                    ? 'Card on file — charged automatically each renewal.'
                    : 'Billed by Mevaser Tech (by hand) — staff get a reminder before each renewal.'}
                </p>
                {canSwitchBilling ? (
                  confirmingSwitch ? (
                    <Alert tone="warning">
                      <p>
                        Stripe stops charging this plan. The current period isn't refunded. The plan
                        stays on record with its next date
                        {sub?.currentPeriodEnd ? ` (${formatDate(sub.currentPeriodEnd)})` : ''}, and
                        staff get a reminder before each renewal, the same as a plan added by hand.
                        All the plan's details stay as they are, and you can switch it back to a
                        card later.
                      </p>
                      <div className="mt-2 flex gap-2">
                        <Button
                          variant="danger"
                          size="sm"
                          disabled={switchBilling.isPending}
                          onClick={() => switchBilling.mutate()}
                        >
                          {switchBilling.isPending ? 'Switching…' : 'Switch to billed elsewhere'}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setConfirmingSwitch(false)}
                        >
                          Keep Stripe billing
                        </Button>
                      </div>
                    </Alert>
                  ) : (
                    <Button variant="secondary" size="sm" onClick={() => setConfirmingSwitch(true)}>
                      Switch to billed elsewhere…
                    </Button>
                  )
                ) : null}
                {sub && !sub.billedByStripe ? (
                  canSwitchToCard ? (
                    choosingCard ? (
                      <Alert tone="info">
                        {ownCards.length ? (
                          <ul className="space-y-1.5">
                            {ownCards.map((card) => (
                              <li key={card.id}>
                                <label className="flex cursor-pointer items-center gap-2 text-sm">
                                  <input
                                    type="radio"
                                    name="plan-switch-card"
                                    checked={cardId === card.id}
                                    onChange={() => setCardId(card.id)}
                                  />
                                  <span className="capitalize">{card.brand}</span> ···· {card.last4}
                                </label>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p>
                            {cards
                              ? 'This customer has no saved card. Add one in Saved cards on their page first.'
                              : 'Loading cards…'}
                          </p>
                        )}
                        {chosenCard ? (
                          <p className="mt-2">
                            From{' '}
                            {sub.currentPeriodEnd
                              ? formatDate(sub.currentPeriodEnd)
                              : 'the next billing date'}{' '}
                            this plan is charged automatically to{' '}
                            <span className="capitalize">{chosenCard.brand}</span> ····{' '}
                            {chosenCard.last4}. Nothing is charged today. All the plan's details
                            stay as they are.
                          </p>
                        ) : null}
                        <div className="mt-2 flex gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            disabled={!chosenCard || switchToCard.isPending}
                            onClick={() => switchToCard.mutate()}
                          >
                            {switchToCard.isPending ? 'Switching…' : 'Charge this card'}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setChoosingCard(false)}
                          >
                            Keep billing by hand
                          </Button>
                        </div>
                      </Alert>
                    ) : (
                      <Button variant="secondary" size="sm" onClick={() => setChoosingCard(true)}>
                        Charge a card on file instead…
                      </Button>
                    )
                  ) : (
                    <p className="text-xs text-foreground-muted">
                      To charge a card, the plan needs to be active, renewing, with a next billing
                      date after today and no charge being rescheduled.
                    </p>
                  )
                ) : null}
              </div>
            </div>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function CancelSubscriptionDialog({
  sub,
  onClose,
}: {
  sub: AdminSubscription | null
  onClose: () => void
}) {
  const toast = useToast()
  const refresh = useRefreshSubscriptions()
  const [immediately, setImmediately] = useState(false)

  useEffect(() => {
    if (sub) setImmediately(false)
  }, [sub])

  // A plan Stripe doesn't bill has nothing that would end it later.
  const canWait = sub?.billedByStripe === true && sub.status !== 'incomplete'
  const stopNow = immediately || !canWait

  const cancel = useMutation({
    mutationFn: () => api.post(`/subscriptions/${sub?.id}/cancel`, { immediately: stopNow }),
    onSuccess: async () => {
      await refresh()
      toast.success(stopNow ? 'Plan cancelled' : 'Plan will end at the end of the paid period')
      onClose()
    },
    onError: (error) =>
      toast.error(
        'Could not cancel the plan',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog open={sub !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cancel plan</DialogTitle>
          <DialogDescription>
            {sub?.planName}
            {sub?.label ? ` · ${sub.label}` : ''}
            {sub?.phoneLine ? ` · ${sub.phoneLine}` : ''}
            {sub?.customerName ? ` · ${sub.customerName}` : ''}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {canWait ? (
            <>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="cancel-plan"
                  checked={!immediately}
                  onChange={() => setImmediately(false)}
                  className="mt-0.5 size-4 accent-primary"
                />
                <span>
                  <span className="font-medium">At the end of the paid period</span>
                  {sub?.currentPeriodEnd ? ` (${formatDate(sub.currentPeriodEnd)})` : ''} — the
                  customer keeps the plan they paid for and is not charged again.
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="cancel-plan"
                  checked={immediately}
                  onChange={() => setImmediately(true)}
                  className="mt-0.5 size-4 accent-primary"
                />
                <span>
                  <span className="font-medium">Right now</span> — for a refund or a mistake. The
                  plan stops immediately.
                </span>
              </label>
            </>
          ) : (
            <Alert tone="info">
              This plan isn't billed through Stripe, so nothing renews it. Cancelling ends it right
              away.
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Keep plan
          </Button>
          <Button variant="danger" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
            {cancel.isPending ? 'Cancelling…' : 'Cancel plan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Records a plan set up outside this system. Nothing is charged. */
/** Plan details already entered elsewhere (the sell dialog) to prefill the record dialog. */
export interface PlanDraft {
  productId: string
  label: string
  phoneLine: string
  simIccid: string
  provider: string
  device: string
  activatedAt: string
  cost: string
}

export function AddSubscriptionDialog({
  customerId,
  open,
  onClose,
  initial = null,
}: {
  customerId: string
  open: boolean
  onClose: () => void
  initial?: PlanDraft | null
}) {
  const toast = useToast()
  const refresh = useRefreshSubscriptions()
  const [productId, setProductId] = useState('')
  const [label, setLabel] = useState('')
  const [phoneLine, setPhoneLine] = useState('')
  const [simIccid, setSimIccid] = useState('')
  const [provider, setProvider] = useState('')
  const [device, setDevice] = useState('')
  const [activatedAt, setActivatedAt] = useState('')
  const [cost, setCost] = useState('')
  const [renewsAt, setRenewsAt] = useState('')

  const { data: options } = useQuery({
    queryKey: queryKeys.subscriptions.list({ planOptions: true }),
    queryFn: () => api.get<SubscriptionPlanOption[]>('/subscriptions/plan-options'),
    enabled: open,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: seed only when the dialog opens
  useEffect(() => {
    if (open) {
      setProductId(initial?.productId ?? '')
      setLabel(initial?.label ?? '')
      setPhoneLine(initial?.phoneLine ?? '')
      setSimIccid(initial?.simIccid ?? '')
      setProvider(initial?.provider ?? '')
      setDevice(initial?.device ?? '')
      setActivatedAt(initial?.activatedAt ?? '')
      setCost(initial?.cost ?? '')
      setRenewsAt('')
    }
  }, [open])

  const chosen = options?.find((o) => o.productId === productId)

  const save = useMutation({
    mutationFn: () =>
      api.post('/subscriptions', {
        customerId,
        productId,
        label: label.trim() || null,
        phoneLine: phoneLine.trim() || null,
        simIccid: simIccid.trim() || null,
        provider: provider.trim() || null,
        device: device.trim() || null,
        activatedAt: activatedAt || null,
        costCents: dollarsToCents(cost),
        renewsAt,
      }),
    onSuccess: async () => {
      await refresh()
      toast.success('Plan added')
      onClose()
    },
    onError: (error) =>
      toast.error('Could not add the plan', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add plan</DialogTitle>
          <DialogDescription>
            For service set up outside this system. Nothing is charged — this keeps the details and
            reminds staff two days before each renewal.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Plan">
            {(props) => (
              <select
                {...props}
                className="h-9 w-full rounded-md border border-border bg-surface px-2 text-sm"
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
              >
                <option value="">Choose a plan…</option>
                {options?.map((o) => (
                  <option key={o.productId} value={o.productId}>
                    {o.name} — {formatMoney(o.monthlyCents)} /{' '}
                    {billingIntervalUnit(o.billingInterval)}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Name" hint="Who this line is for, e.g. Mrs Smith.">
            {(props) => (
              <Input
                {...props}
                maxLength={80}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            )}
          </Field>
          <Field label="Provider" hint="Who supplies the service, e.g. US Mobile.">
            {(props) => (
              <Input
                {...props}
                maxLength={60}
                list="plan-providers"
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
              />
            )}
          </Field>
          <ProviderSuggestions />
          <Field label="Phone number">
            {(props) => (
              <Input
                {...props}
                type="tel"
                inputMode="tel"
                maxLength={30}
                value={phoneLine}
                onChange={(e) => setPhoneLine(e.target.value)}
              />
            )}
          </Field>
          <Field label="SIM number (ICCID)" hint="Required — every plan is on a SIM.">
            {(props) => (
              <Input
                {...props}
                required
                maxLength={40}
                value={simIccid}
                onChange={(e) => setSimIccid(e.target.value)}
              />
            )}
          </Field>
          <Field label="Activated device">
            {(props) => (
              <Input
                {...props}
                maxLength={120}
                value={device}
                onChange={(e) => setDevice(e.target.value)}
              />
            )}
          </Field>
          <Field label="Activation date">
            {(props) => (
              <Input
                {...props}
                type="date"
                value={activatedAt}
                onChange={(e) => setActivatedAt(e.target.value)}
              />
            )}
          </Field>
          <Field label="Next charge date" hint="Staff are reminded two days before.">
            {(props) => (
              <Input
                {...props}
                type="date"
                value={renewsAt}
                onChange={(e) => setRenewsAt(e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Our cost ($)"
            hint={
              chosen
                ? `What we pay the provider each ${billingIntervalUnit(chosen.billingInterval)}. ${costProfitHint(chosen.monthlyCents, cost)}`
                : 'What we pay the provider each billing period.'
            }
          >
            {(props) => (
              <Input
                {...props}
                inputMode="decimal"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="primary"
            disabled={save.isPending || !productId || !renewsAt || !simIccid.trim()}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Adding…' : 'Add plan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
